import useTeamChannelUpdates from 'core/useTeamChannelUpdates';
import { Api, POLL_STATE, canManagePollChannel, getPollRole } from 'lib';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SurfacePollCard from './SurfacePollCard';
import SurfaceQuizCard from './SurfaceQuizCard';

/**
 * TeamChannelSurface — fan-facing single rotating slot for a team profile.
 *
 * Mirrors the mobile app: the backend GET /teamChannel/surface resolves the whole
 * game-day rotation (Poll open → closed → hidden-at-half-time → Quick Quiz → poll
 * results return) and tells us which single module owns the slot right now. We just
 * render it: a poll card or the half-time quiz card. When nothing is live
 * (surface === 'none') we render nothing — no placeholder clutter on quiet profiles.
 *
 * Fan-only: channel owners / P4P admins keep their full management rows (PollsRow,
 * QuickQuizRow) instead — this component renders null for them. Daily quizzes never
 * appear here (they live in the separate daily quiz row).
 *
 * Realtime: reuses useTeamChannelUpdates. A poll `results_updated` patches the tally in
 * place; any structural event (created/updated/deleted/state_changed) refetches the
 * surface so a phase flip (e.g. poll hidden at half-time → quiz) lands automatically.
 */
const TeamChannelSurface = ({ team }) => {
  const currentUser = useSelector((state) => state.user);
  const teamId = team?._id;
  const canManage = canManagePollChannel(currentUser, teamId);
  // Only fans/athletes participate. Team accounts (and P4P admins) manage but never vote/
  // answer — hide the fan surface for them on every channel, not just ones they own.
  const isManagerAccount = !!getPollRole(currentUser);
  const showSurface = !!teamId && !!currentUser && !canManage && !isManagerAccount;

  const [surface, setSurface] = useState('none');
  const [poll, setPoll] = useState(null);
  const [quiz, setQuiz] = useState(null);

  const fetchSurface = useCallback(async () => {
    if (!teamId || !currentUser) return;
    const res = await Api.get(`/teamChannel/surface?teamId=${teamId}`, null, { silent: true });
    if (res && !res.err) {
      setSurface(res.surface || 'none');
      setPoll(res.poll || null);
      setQuiz(res.quiz || null);
    }
  }, [teamId, currentUser]);

  useEffect(() => {
    if (showSurface) fetchSurface();
  }, [showSurface, fetchSurface]);

  // Debounced refetch on structural changes (mirrors Polls/index.js).
  const refetchTimerRef = useRef(null);
  const scheduleRefetch = useCallback(() => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => fetchSurface(), 350);
  }, [fetchSurface]);
  useEffect(() => () => clearTimeout(refetchTimerRef.current), []);

  useTeamChannelUpdates(
    showSurface ? teamId : null,
    useCallback(
      (evt) => {
        if (!evt) return;
        // Live vote tally for the surfaced poll — patch in place, no refetch. Only merge
        // results once the fan is entitled to see them (already voted / poll closed).
        if (evt.entity === 'poll' && evt.action === 'results_updated' && evt.data) {
          setPoll((p) => {
            if (!p || p._id !== evt.id) return p;
            const entitled = p.answered === true || p.state === POLL_STATE.CLOSED || !!p.results;
            return {
              ...p,
              totalSubmissions: evt.data.totalSubmissions ?? p.totalSubmissions,
              ...(entitled ? { results: evt.data } : {})
            };
          });
          return;
        }
        if (evt.entity === 'quiz' && evt.action === 'results_updated') return; // ignore
        if (evt.entity === 'poll' || evt.entity === 'quiz') scheduleRefetch();
      },
      [scheduleRefetch]
    )
  );

  // Fan-only surface; managers keep their existing management rows.
  if (!showSurface) return null;

  if (surface === 'poll' && poll) {
    return <SurfacePollCard poll={poll} onVoted={setPoll} />;
  }

  if (surface === 'quick_quiz' && quiz) {
    return <SurfaceQuizCard quiz={quiz} onAnswered={() => scheduleRefetch()} />;
  }

  // surface === 'none' → nothing live right now; render nothing.
  return null;
};

export default TeamChannelSurface;
